import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Copy, Link as LinkIcon } from 'lucide-react';
import { StatuteRenderer } from './StatuteRenderer';
import {
  groupPreviewNavSections,
  PREVIEW_NAV_UNGROUPED_KEY,
  resolveLibrarySectionIdFromHash,
} from '../lib/statuteSections';
import type { BodyBlock, GroupKind, UnitKind } from '../lib/statute/types';

export type StatutePreviewUiSection = {
  id: string;
  label?: string;
  title?: string;
  shortTitle?: string;
  tocLine?: string;
  tocPartLabel?: string;
  tocChapterLabel?: string;
  tocHeadingLabel?: string;
  tocSectionBadge?: string;
  tocListIndex?: number;
  content?: string;
  subsections?: unknown;
  clauses?: unknown;
  provisos?: unknown;
  explanations?: unknown;
  order?: number;
  /**
   * Optional structured body produced by the canonical adapter pipeline.
   * Reserved for diagnostics and future renderers; today's StatuteRenderer
   * still renders from `content`/`subsections`/`clauses`/`provisos`/`explanations`,
   * which the bridge populates alongside `body` for visual parity.
   */
  body?: BodyBlock[];
  groupKind?: GroupKind;
  unitKind?: UnitKind;
};

function sectionTocFallbackLine(section: StatutePreviewUiSection, unitLabel: string): string {
  if (section.tocLine && String(section.tocLine).trim()) return String(section.tocLine).trim();
  const label = String(section.label || '').trim();
  if (!label) return section.title || unitLabel;
  if (label.toLowerCase() === 'preamble') return section.title || 'Preamble';
  return label;
}

function tocRowTitleAttr(section: StatutePreviewUiSection): string | undefined {
  const t = section.tocLine || section.title;
  return t && String(t).trim() ? String(t) : undefined;
}

export function statutePreviewNavRowNumber(section: StatutePreviewUiSection): string | null {
  const sid = String(section.id || '');
  const label = String(section.label || '').trim();
  let m: RegExpMatchArray | null;
  if (/^preamble/i.test(sid)) return null;

  // Prefer explicit JSON label semantics whenever available.
  m = label.match(/^\s*Section\s+(\d+[A-Za-z]*)\b/i);
  if (m) return m[1];
  m = label.match(/^\s*Article\s+(\d+[A-Za-z]*)\b/i);
  if (m) return m[1];
  m = label.match(/^\s*Rule\s+(\d+[A-Za-z]*)\b/i);
  if (m) return `Rule ${m[1]}`;
  m = label.match(/^\s*Form\s+([A-Za-z0-9]+)\b/i);
  if (m) return `Form ${m[1]}`;

  m = sid.match(/^s-(\d+[A-Za-z]*)(?:~|$)/i);
  if (m) return m[1];
  m = sid.match(/^art-(\d+[A-Za-z]*)(?:~|$)/i);
  if (m) return m[1];
  // CPC / similar v2 payloads: ord-I-r-1, ord-XXI-r-58, etc.
  m = sid.match(/^ord-[A-Za-z0-9IVXLCDM]+-r-(\d+[A-Za-z]*)(?:~|$)/i);
  if (m) return `Rule ${m[1]}`;
  m = sid.match(/^sch-(\d+)-s-(\d+[A-Za-z]*)/i);
  if (m) return m[2];
  m = sid.match(/^app-[A-Za-z]+-form-([A-Za-z0-9]+)(?:~|$)/i);
  if (m) return `Form ${m[1]}`;

  // Structural containers should never borrow global list indexes.
  if (/^(ord-|sch-|app-)/i.test(sid)) return null;

  // Avoid cross-container numbering bleed (e.g. 756, 757, ...).
  return null;
}

export function sectionToStatuteClipboardText(section: StatutePreviewUiSection): string {
  const lines: string[] = [];
  if (section.tocPartLabel) lines.push(section.tocPartLabel);
  if (section.tocSectionBadge) lines.push(section.tocSectionBadge);
  if (section.shortTitle || section.title) lines.push(section.shortTitle || section.title || '');
  if (section.content && String(section.content).trim()) lines.push(String(section.content).trim());
  return lines.filter(Boolean).join('\n\n').trim();
}

export async function writeStatuteClipboard(text: string): Promise<boolean> {
  const value = String(text || '');
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', 'true');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function splitPartHeading(heading: string): { partLabel: string; partTitle?: string } {
  const t = String(heading || '').trim();
  const m = t.match(/^(Part\s+[A-Za-z0-9IVXLCDM-]+)(?:\s+(.*))?$/i);
  if (!m) return { partLabel: t };
  const partLabel = m[1].trim();
  const partTitle = m[2] && String(m[2]).trim() ? String(m[2]).trim() : undefined;
  return { partLabel, partTitle };
}

function splitChapterHeading(heading: string): { chapterLabel: string; chapterTitle?: string } {
  const t = String(heading || '').trim();
  const m = t.match(/^(Chapter\s+[A-Za-z0-9IVXLCDM-]+)(?:\s+(.*))?$/i);
  if (!m) return { chapterLabel: t };
  const chapterLabel = m[1].trim();
  const chapterTitle = m[2] && String(m[2]).trim() ? String(m[2]).trim() : undefined;
  return { chapterLabel, chapterTitle };
}

function sameText(a?: string, b?: string): boolean {
  const norm = (v?: string) => String(v || '').trim().replace(/\s+/g, ' ').toLowerCase();
  const av = norm(a);
  const bv = norm(b);
  return Boolean(av) && av === bv;
}

function PreviewSectionNavButton({
  section,
  isActive,
  hidePartLabel,
  onSelect,
  unitLabel,
}: {
  section: StatutePreviewUiSection;
  isActive: boolean;
  hidePartLabel: boolean;
  onSelect: (id: string) => void;
  unitLabel: string;
}) {
  const rowTitle = section.shortTitle || section.title || sectionTocFallbackLine(section, unitLabel);
  const showPartLine = Boolean(!hidePartLabel && section.tocPartLabel && !sameText(section.tocPartLabel, rowTitle));
  const hasStack = Boolean(showPartLine);
  const title = tocRowTitleAttr(section);
  const rowNumber = statutePreviewNavRowNumber(section);
  const baseBtn = `flex w-full text-left gap-2 px-2.5 py-3 rounded-lg transition-colors ${
    isActive ? 'bg-zinc-900 text-white' : 'text-zinc-800 hover:bg-zinc-100'
  }`;

  if (!hasStack) {
    return (
      <button type="button" title={title} onClick={() => onSelect(String(section.id))} className={baseBtn}>
        {rowNumber ? (
          <span
            className={`w-8 shrink-0 text-right text-sm font-semibold tabular-nums pt-0.5 ${
              isActive ? 'text-zinc-300' : 'text-zinc-600'
            }`}
          >
            {rowNumber}.
          </span>
        ) : null}
        <span className="text-base font-semibold min-w-0 flex-1 leading-7">{rowTitle}</span>
      </button>
    );
  }

  return (
    <button type="button" title={title} onClick={() => onSelect(String(section.id))} className={baseBtn}>
      {rowNumber ? (
        <span
          className={`w-8 shrink-0 text-right text-sm font-semibold tabular-nums pt-0.5 ${
            isActive ? 'text-zinc-300' : 'text-zinc-600'
          }`}
        >
          {rowNumber}.
        </span>
      ) : null}
      <div className="min-w-0 flex-1 flex flex-col gap-0.5">
        {showPartLine ? (
          <span
            className={`text-xs font-bold uppercase tracking-[0.1em] ${isActive ? 'text-zinc-300' : 'text-zinc-600'}`}
          >
            {section.tocPartLabel}
          </span>
        ) : null}
        <span className={`text-base font-semibold leading-7 ${isActive ? 'text-white' : 'text-zinc-900'}`}>
          {rowTitle}
        </span>
      </div>
    </button>
  );
}

export type StatutePreviewReaderProps = {
  sections: StatutePreviewUiSection[];
  loading?: boolean;
  error?: string | null;
  unitLabel: 'Section' | 'Article';
  tocVariant?: 'default' | 'qso';
  /** Path-only base for deep links, e.g. `/library/docId` or `/statutes/docId` */
  linkBasePath: string;
  locationHash: string;
  selectedSectionId: string | null;
  onSelectedSectionIdChange: (id: string) => void;
  /** Sync URL after selecting a section (hash only or full path+hash) */
  syncUrlForSection: (sectionId: string) => void;
  /** e.g. PDF button row */
  readerTopSlot?: ReactNode;
  /** Match preview: rounded card aside on standalone preview; flat border in library */
  asideVariant?: 'card' | 'plain';
};

export function StatutePreviewReader({
  sections,
  loading = false,
  error = null,
  unitLabel,
  tocVariant = 'default',
  linkBasePath,
  locationHash,
  selectedSectionId,
  onSelectedSectionIdChange,
  syncUrlForSection,
  readerTopSlot,
  asideVariant = 'card',
}: StatutePreviewReaderProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [copiedState, setCopiedState] = useState<Record<string, 'content' | 'link' | null>>({});
  const contentScrollRef = useRef<HTMLDivElement | null>(null);
  const sectionNodeMapRef = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    if (!sections.length) return;
    const rawHash = locationHash?.replace(/^#/, '');
    if (rawHash) {
      const resolved = resolveLibrarySectionIdFromHash(rawHash, sections);
      if (resolved) {
        onSelectedSectionIdChange(resolved);
        requestAnimationFrame(() => {
          const id = resolveLibrarySectionIdFromHash(rawHash, sections);
          if (!id) return;
          const node = sectionNodeMapRef.current[String(id)];
          if (node) node.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
      return;
    }
    if (selectedSectionId && sections.some((s) => String(s.id) === String(selectedSectionId))) return;
    onSelectedSectionIdChange(String(sections[0].id));
  }, [sections, locationHash, selectedSectionId, onSelectedSectionIdChange]);

  const navGroups = useMemo(() => groupPreviewNavSections(sections), [sections]);
  const isQsoToc = tocVariant === 'qso';
  const showNavSubheadings = !(navGroups.length === 1 && navGroups[0].key === PREVIEW_NAV_UNGROUPED_KEY);

  const activeGroupKey = useMemo(() => {
    if (!selectedSectionId) return null;
    for (const group of navGroups) {
      if (group.sections.some((s) => String(s.id) === String(selectedSectionId))) return group.key;
    }
    return null;
  }, [navGroups, selectedSectionId]);

  useEffect(() => {
    if (!activeGroupKey) return;
    setCollapsedGroups((prev) => {
      if (prev[activeGroupKey] === false) return prev;
      return { ...prev, [activeGroupKey]: false };
    });
  }, [activeGroupKey]);

  const isGroupCollapsed = (key: string) => Boolean(collapsedGroups[key]);
  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openSection = (sectionId: string) => {
    onSelectedSectionIdChange(sectionId);
    const el = sectionNodeMapRef.current[String(sectionId)];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    syncUrlForSection(sectionId);
  };

  const copySectionContent = async (section: StatutePreviewUiSection) => {
    const ok = await writeStatuteClipboard(sectionToStatuteClipboardText(section));
    const key = String(section.id);
    if (ok) {
      setCopiedState((prev) => ({ ...prev, [key]: 'content' }));
      window.setTimeout(() => {
        setCopiedState((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
      }, 1400);
    } else {
      window.alert('Copy failed. Please copy manually.');
    }
  };

  useEffect(() => {
    if (!sections.length) return;
    const root = contentScrollRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (!visible.length) return;
        const id = visible[0].target.getAttribute('data-section-id');
        if (!id) return;
        onSelectedSectionIdChange(String(id));
      },
      { root, threshold: [0.3, 0.5, 0.75], rootMargin: '-10% 0px -60% 0px' },
    );

    sections.forEach((section) => {
      const node = sectionNodeMapRef.current[String(section.id)];
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, [sections, onSelectedSectionIdChange]);

  const asideWrap =
    asideVariant === 'card'
      ? 'rounded-2xl border border-zinc-200 bg-white shadow-sm lg:sticky lg:top-24 lg:self-start lg:h-[calc(100vh-7rem)]'
      : 'border-r border-zinc-200 bg-white h-full overflow-hidden';

  const asideHeader =
    asideVariant === 'card'
      ? 'px-4 py-3 border-b border-zinc-200 shrink-0 bg-zinc-50/90 rounded-t-2xl'
      : 'px-4 py-3 border-b border-zinc-200 shrink-0 bg-zinc-50/90';

  const asideBody =
    asideVariant === 'card'
      ? 'flex-1 overflow-y-auto min-h-[36vh] lg:min-h-0 p-2 rounded-b-2xl'
      : 'flex-1 overflow-y-auto min-h-[36vh] p-2';

  const rightCol =
    asideVariant === 'card'
      ? 'flex-1 min-w-0 flex flex-col h-[100vh] lg:h-[calc(100vh-7rem)] bg-[#f4f4f5] rounded-xl p-3'
      : 'flex-1 min-w-0 flex flex-col h-full bg-[#f4f4f5] p-3';

  return (
    <div
      className={`flex flex-col lg:flex-row gap-6 lg:items-stretch ${asideVariant === 'card' ? 'lg:min-h-[calc(100vh-13rem)]' : 'min-h-0 flex-1'} min-h-0`}
    >
      <aside
        className={`w-full lg:w-[min(380px,38%)] lg:max-w-[400px] shrink-0 flex flex-col ${asideWrap}`}
      >
        <div className={asideHeader}>
          <p className="text-sm font-bold tracking-[0.14em] uppercase text-zinc-800">{`${unitLabel}s`}</p>
        </div>
        <div className={asideBody}>
          {loading ? <p className="px-3 py-2 text-base text-zinc-700">Loading…</p> : null}
          {error ? <p className="px-3 py-2 text-base text-rose-700 font-medium">{error}</p> : null}
          {!loading &&
            !error &&
            navGroups.map((group, gi) => (
              <div key={`${group.key}-${gi}`} className="mb-1 last:mb-0">
                {showNavSubheadings ? (
                  <button
                    type="button"
                    onClick={() => toggleGroupCollapsed(group.key)}
                    aria-expanded={!isGroupCollapsed(group.key)}
                    className={`px-3 ${gi === 0 ? 'pt-1' : 'pt-4'} pb-1.5 border-t border-zinc-100 ${
                      gi === 0 ? 'border-t-0' : ''
                    } w-full text-left flex items-center justify-between gap-2`}
                  >
                    {(() => {
                      const { partLabel, partTitle } = splitPartHeading(group.heading);
                      return (
                        <p className="text-xs uppercase tracking-[0.1em] leading-snug">
                          <span className="font-bold text-zinc-900">{partLabel}</span>
                          {partTitle ? <span className="font-semibold text-zinc-700"> {partTitle}</span> : null}
                        </p>
                      );
                    })()}
                    <ChevronDown
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${
                        isGroupCollapsed(group.key) ? '-rotate-90' : 'rotate-0'
                      }`}
                    />
                  </button>
                ) : null}
                {showNavSubheadings && isGroupCollapsed(group.key) ? null : isQsoToc ? (
                  <div className="flex flex-col gap-1">
                    {(() => {
                      const chapterGroups = group.sections.reduce<Array<{ chapter: string; rows: StatutePreviewUiSection[] }>>(
                        (acc, section) => {
                          const chapter = String(section.tocChapterLabel || '').trim() || 'Articles';
                          const last = acc[acc.length - 1];
                          if (last && last.chapter === chapter) {
                            last.rows.push(section);
                          } else {
                            acc.push({ chapter, rows: [section] });
                          }
                          return acc;
                        },
                        [],
                      );
                      return chapterGroups.map((cg, cgi) => (
                        <div key={`${group.key}-${cg.chapter}-${cgi}`} className="pt-1">
                          {cg.chapter && cg.chapter !== 'Articles' && !sameText(cg.chapter, group.heading) ? (
                            <div className="px-3 pb-1">
                              {(() => {
                                const { chapterLabel, chapterTitle } = splitChapterHeading(cg.chapter);
                                return (
                                  <p className="text-[11px] uppercase tracking-[0.08em] leading-snug">
                                    <span className="font-bold text-zinc-800">{chapterLabel}</span>
                                    {chapterTitle ? (
                                      <span className="font-semibold text-zinc-600"> {chapterTitle}</span>
                                    ) : null}
                                  </p>
                                );
                              })()}
                            </div>
                          ) : null}
                          {(() => {
                            const headingGroups = cg.rows.reduce<Array<{ heading: string; rows: StatutePreviewUiSection[] }>>(
                              (acc, section) => {
                                const heading = String(section.tocHeadingLabel || '').trim();
                                const last = acc[acc.length - 1];
                                if (last && last.heading === heading) {
                                  last.rows.push(section);
                                } else {
                                  acc.push({ heading, rows: [section] });
                                }
                                return acc;
                              },
                              [],
                            );

                            return headingGroups.map((hg, hgi) => (
                              <div
                                key={`${group.key}-${cg.chapter}-${hg.heading || 'no-heading'}-${hgi}`}
                                className="pb-1"
                              >
                                {hg.heading && !sameText(hg.heading, cg.chapter) ? (
                                  <div className="px-3 pt-1 pb-1">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.09em] text-zinc-700">
                                      {hg.heading}
                                    </p>
                                  </div>
                                ) : null}
                                <div className="flex flex-col gap-0.5">
                                  {hg.rows.map((section) => (
                                    <PreviewSectionNavButton
                                      key={section.id}
                                      section={section}
                                      isActive={String(section.id) === String(selectedSectionId)}
                                      hidePartLabel
                                      onSelect={openSection}
                                      unitLabel={unitLabel}
                                    />
                                  ))}
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      ));
                    })()}
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    {group.sections.map((section) => (
                      <PreviewSectionNavButton
                        key={section.id}
                        section={section}
                        isActive={String(section.id) === String(selectedSectionId)}
                        hidePartLabel={showNavSubheadings && group.key !== PREVIEW_NAV_UNGROUPED_KEY}
                        onSelect={openSection}
                        unitLabel={unitLabel}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
        </div>
      </aside>

      <div className={rightCol}>
        <div className="h-full overflow-y-auto">
          {!sections.length ? (
            <div className="p-8 md:p-10 h-full flex items-center justify-center">
              <p className="text-zinc-700 text-lg leading-8 text-center max-w-2xl">
                Select a section from the list on the left. The provision text appears in this card.
              </p>
            </div>
          ) : (
            <div ref={contentScrollRef} className="h-full overflow-y-auto overscroll-contain">
              <div className="p-4 space-y-4">
                {readerTopSlot}
                {sections.map((section) => (
                  <article
                    key={section.id}
                    data-section-id={String(section.id)}
                    ref={(node) => {
                      sectionNodeMapRef.current[String(section.id)] = node;
                    }}
                    className="scroll-mt-24 bg-white rounded-[8px] shadow-[0_2px_8px_rgba(0,0,0,0.08)] border-t border-b border-zinc-200 px-7 py-6"
                  >
                    <div className="flex items-start justify-end gap-3 mb-4">
                      {(() => {
                        const copied = copiedState[String(section.id)];
                        const isContentCopied = copied === 'content';
                        const isLinkCopied = copied === 'link';
                        return (
                          <>
                            <button
                              type="button"
                              onClick={() => copySectionContent(section)}
                              className={`inline-flex items-center gap-2 text-sm font-semibold shrink-0 transition-colors ${
                                isContentCopied ? 'text-emerald-700' : 'text-zinc-700 hover:text-zinc-900'
                              }`}
                            >
                              {isContentCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                              {isContentCopied ? 'Copied' : 'Copy'}
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const hashValue = `#section-${String(section.id).replace(/^section-/i, '')}`;
                                const ok = await writeStatuteClipboard(
                                  `${window.location.origin}${linkBasePath}${hashValue}`,
                                );
                                const key = String(section.id);
                                if (ok) {
                                  setCopiedState((prev) => ({ ...prev, [key]: 'link' }));
                                  window.setTimeout(() => {
                                    setCopiedState((prev) => (prev[key] ? { ...prev, [key]: null } : prev));
                                  }, 1400);
                                } else {
                                  window.alert('Copy link failed. Please copy manually.');
                                }
                              }}
                              className={`inline-flex items-center gap-2 text-sm font-semibold shrink-0 transition-colors ${
                                isLinkCopied ? 'text-emerald-700' : 'text-zinc-700 hover:text-zinc-900'
                              }`}
                            >
                              {isLinkCopied ? <Check className="w-3.5 h-3.5" /> : <LinkIcon className="w-3.5 h-3.5" />}
                              {isLinkCopied ? 'Link copied' : 'Copy link'}
                            </button>
                          </>
                        );
                      })()}
                    </div>
                    {section.tocPartLabel ? (
                      !sameText(section.tocPartLabel, section.shortTitle || section.title) ? (
                        <p className="text-sm font-bold uppercase tracking-[0.1em] text-zinc-700 mb-1">
                          {section.tocPartLabel}
                        </p>
                      ) : null
                    ) : null}
                    {section.tocSectionBadge ? (
                      <p className="text-base font-semibold text-zinc-800 mb-3">{section.tocSectionBadge}</p>
                    ) : null}
                    {!section.tocSectionBadge && section.label ? (
                      <p className="text-sm uppercase tracking-[0.14em] text-primary font-semibold mb-2">{section.label}</p>
                    ) : null}
                    <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-950 mb-6">
                      {section.shortTitle || section.title}
                    </h2>
                    <div className="prose prose-slate max-w-none prose-p:text-lg prose-p:leading-8 prose-li:text-lg prose-li:leading-8 prose-headings:text-zinc-950 prose-strong:text-zinc-950">
                      <StatuteRenderer data={section} />
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
